/**
 * Day helpers for the MSR portal's day-of routing (schedule grouping + same-day
 * event switcher). Dates are computed in the EVENT's own timezone so grouping
 * matches what a rep on-site experiences (a rep is physically in the event tz).
 */

type DayInput = {
  start_time?: string | null;
  start_date?: string | null;
  timezone?: string | null;
};

function ymd(d: Date, timeZone?: string | null): string {
  try {
    // en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timeZone || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    // Invalid timezone string — fall back to the device timezone rather than throw.
    return new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  }
}

/** The event's own local calendar date as YYYY-MM-DD. Prefers the explicit
 * start_date, else derives it from start_time in the event tz. "" = date TBD. */
export function eventLocalDate(row: DayInput): string {
  if (row.start_date) {
    return row.start_date;
  }
  if (!row.start_time) {
    return "";
  }
  const d = new Date(row.start_time);
  if (isNaN(d.getTime())) {
    return "";
  }
  return ymd(d, row.timezone);
}

/** The event's local END date (YYYY-MM-DD), or "" when it has no end. */
export function eventLocalEndDate(row: {
  end_time?: string | null;
  timezone?: string | null;
}): string {
  if (!row.end_time) {
    return "";
  }
  const d = new Date(row.end_time);
  return isNaN(d.getTime()) ? "" : ymd(d, row.timezone);
}

/**
 * The day a schedule row is listed under: its start day, except that an event already running (started before
 * today, ends today or later, e.g. day 2 of a gun show) is listed under today.
 */
export function scheduleDayOf(
  row: DayInput & { end_time?: string | null },
  today: string = deviceToday(),
): string {
  const start = eventLocalDate(row);
  const end = eventLocalEndDate(row);
  if (start && end && start < today && end >= today) {
    return today;
  }
  return start;
}

/** Today as YYYY-MM-DD in the device timezone. */
export function deviceToday(): string {
  return ymd(new Date());
}

export interface DayGroup<T> {
  date: string; // YYYY-MM-DD, or "tbd"
  rows: T[];
}

/** Group rows by their event-local day, ordered Today → future (asc) → past
 * (most-recent first) → date-TBD, with each day's rows sorted by start time.
 * `today` is injectable for testing. */
export function groupByDay<
  T extends {
    start_time?: string | null;
    start_date?: string | null;
    timezone?: string | null;
  },
>(
  rows: T[],
  today: string = deviceToday(),
  dayOf: (row: T) => string = eventLocalDate,
): DayGroup<T>[] {
  const byDate = new Map<string, T[]>();
  for (const r of rows) {
    const key = dayOf(r) || "tbd";
    const list = byDate.get(key);
    if (list) {
      list.push(r);
    } else {
      byDate.set(key, [r]);
    }
  }
  for (const list of byDate.values()) {
    list.sort((a, b) =>
      (a.start_time ?? a.start_date ?? "").localeCompare(
        b.start_time ?? b.start_date ?? "",
      ),
    );
  }
  const keys = [...byDate.keys()];
  const todayKeys = keys.filter((d) => d === today);
  const future = keys.filter((d) => d !== "tbd" && d > today).sort();
  const past = keys
    .filter((d) => d !== "tbd" && d < today)
    .sort()
    .reverse();
  const tbd = keys.filter((d) => d === "tbd");
  return [...todayKeys, ...future, ...past, ...tbd].map((date) => ({
    date,
    rows: byDate.get(date) ?? [],
  }));
}

/** A human day header for a YYYY-MM-DD, relative to the device's today. */
export function dayLabel(isoDate: string): string {
  if (!isoDate) {
    return "Date TBD";
  }
  const today = deviceToday();
  if (isoDate === today) {
    return "Today";
  }
  const [ty, tm, td] = today.split("-").map(Number);
  const tomorrow = ymd(new Date(ty, tm - 1, td + 1));
  if (isoDate === tomorrow) {
    return "Tomorrow";
  }
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** The part of get_event_day_run_state this needs (kept structural so this file stays free of the API client). */
export interface ClosedDaysInput {
  days?: {
    close: {
      attendance_count: number | null;
      latest_correction: { attendance_count: number | null } | null;
    } | null;
  }[];
}

/**
 * MSR day model (final audit P2-1, migration 505): on an event that is started and closed per day, the day closes are
 * the source of truth for attendance. The card shows their sum read-only (per closed day: the latest admin correction,
 * else the close) and never writes attendance or completes the event; the server refuses both anyway.
 */
export function closedDaysAttendance(state: ClosedDaysInput | undefined): {
  total: number | null;
  closedDays: number;
  days: number;
} {
  const days = state?.days ?? [];
  let total: number | null = null;
  let closedDays = 0;
  for (const d of days) {
    if (!d.close) {
      continue;
    }
    closedDays += 1;
    const n =
      d.close.latest_correction?.attendance_count ?? d.close.attendance_count;
    if (n !== null && n !== undefined) {
      total = (total ?? 0) + n;
    }
  }
  return { total, closedDays, days: days.length };
}
